<template>
  <div>
    <div v-if="$vuetify.breakpoint.mdAndUp" class="event-card mb-7">
      <v-card :color="event.discipline.name" class="event-card--discipline">
        <div class="d-flex flex-row">
          <div style="text-transform: capitalize">
            {{ $t("common:discipline." + event.discipline.name)
            }}<AppShowEventReferenceCode :event="event" force-current-color
              ><template #prepend> | </template></AppShowEventReferenceCode
            >
          </div>
          <div>
            <AppShowEventStatus :event="event" no-button block always-show />
          </div>
        </div>
      </v-card>
      <div>
        <AppAlertNotice v-if="showAlert" :text="event.alertText" event-card />
      </div>
      <v-card class="event-card--content small-height" data-test="eventCard">
        <div>
          <v-row no-gutters>
            <v-col cols="8" style="display: flex; flex-direction: column">
              <div class="event-card--content--date-location">
                {{ eventDate }}
                <span class="line" />
                <AppShowCountry :country="event.country">
                  <template #append
                    >, {{ regionName
                    }}{{ event.place ? ", " + event.place : "" }}
                  </template>
                </AppShowCountry>
              </div>

              <div class="event-card--content--title">
                <b>
                  <AppShowEventName
                    :event="event"
                    :disable-link="disableLink"
                  />
                </b>

                <v-icon
                  v-if="event.isHighlighted"
                  class="star-icon"
                  style="font-size: 24px"
                  color="star"
                  >$iconStar</v-icon
                >
              </div>
              <div class="event-card--content--organiser">
                <AppOrganisationNameLink :organisation="event.organiser" />
                <template v-if="event.hostOrganisation">
                  |
                  <AppOrganisationNameLink
                    :organisation="event.hostOrganisation"
                  />
                </template>
              </div>
            </v-col>

            <v-col cols="4">
              <div class="event-card--content--logos">
                <div class="sponsor-logo"></div>
                <div class="organiser-logo">
                  <OrganizationLogo :organiser="event.organiser" />
                </div>
              </div>

              <slot name="orderBalance" />
            </v-col>
          </v-row>
        </div>
      </v-card>
    </div>
    <div v-else class="event-vertical-card ma-auto">
      <div>
        <v-card
          :color="event.discipline ? event.discipline.name : 'showjumping'"
          class="event-vertical-card--discipline"
        >
          <div class="d-flex flex-row">
            <div style="text-transform: capitalize">
              {{ $t("common:discipline." + event.discipline.name)
              }}<AppShowEventReferenceCode :event="event" force-current-color
                ><template #prepend> | </template></AppShowEventReferenceCode
              >
            </div>
            <div>
              <AppShowEventStatus :event="event" no-button block always-show />
            </div>
          </div>
        </v-card>
      </div>
      <div v-if="event.alertText">
        <AppAlertNotice :text="event.alertText" event-card />
      </div>
      <v-card class="py-4 px-3 event-vertical-card--content">
        <div style="position: relative">
          {{ eventDate }}
          <br />
          <AppShowCountry :country="event.country" />
          {{ regionName ? ", " + regionName : "" }}
          {{ event.place ? ", " + event.place : "" }}
          <v-icon
            v-if="event.isHighlighted"
            class="star-icon"
            color="star"
            style="position: absolute; top: -5px; right: 20px; font-size: 24px"
            >$iconStar</v-icon
          >
        </div>

        <div>
          <v-row dense align="center">
            <v-col v-if="!ignoreLogo" cols="5">
              <OrganizationLogo
                :organiser="event.organiser"
                small
                class="pa-3"
              />
            </v-col>

            <v-col :cols="ignoreLogo ? 12 : 7">
              <div class="body-2">
                <b>
                  <AppShowEventName
                    :event="event"
                    :disable-link="disableLink"
                  />
                </b>
              </div>
              <div class="muted--text">
                <AppOrganisationNameLink
                  :organisation="event.organiser"
                /><template v-if="event.hostOrganisation">
                  |
                  <AppOrganisationNameLink
                    :organisation="event.hostOrganisation"
                  />
                </template>
              </div>
            </v-col>
          </v-row>

          <slot name="orderBalance" />
        </div>
      </v-card>
    </div>
  </div>
</template>

<script>
import gql from "fraql";
import { useEventCardHelpers } from "./EventCardMixin";
import OrganizationLogo from "@/components/elements/AppOrganLogo.vue";
import AppShowEventStatus from "@/components/elements/AppShows/event/AppShowEventStatus.vue";
import AppOrganisationNameLink from "@/components/elements/AppNameLinks/AppOrganisationNameLink.vue";
import AppShowCountry from "@AppShows/AppShowCountry.vue";
import AppAlertNotice from "@elements/AppAlertNotice/index.vue";
import AppShowEventName from "@AppShows/event/AppShowEventName.vue";
import AppShowEventReferenceCode from "@AppShows/event/AppShowEventReferenceCode.vue";

export default {
  name: "EventCardSmall",
  components: {
    OrganizationLogo,
    AppShowEventStatus,
    AppOrganisationNameLink,
    AppShowCountry,
    AppAlertNotice,
    AppShowEventName,
    AppShowEventReferenceCode
  },
  props: {
    event: {
      type: Object,
      required: true
    },
    ignoreLogo: {
      type: Boolean,
      default: false
    },
    disableLink: {
      type: Boolean,
      default: false
    }
  },
  setup(props) {
    const { regionName, eventDate, showAlert } = useEventCardHelpers(props);

    return {
      showAlert,
      regionName,
      eventDate
    };
  },
  fragments: {
    event: gql`
      fragment _ on Event {
        id
        name
        ${AppShowEventReferenceCode.fragments.eventFields}
        ${AppShowEventName.fragments.event}
        ${AppShowEventStatus.fragments.eventStatus}
        description
        startDate
        endDate
        registrationEndDate
        countryId
        country @client {
          ${AppShowCountry.fragments.country}
        }
        regionId
        region @client {
          id
          name
        }
        disciplineId
        discipline @client {
          id
          name
        }
        place
        organiser {
          id
          name
          logoPath
        }
        hostOrganisation {
          id
          name
        }
      }
    `,
    payableEventInfo: gql`
      fragment _ on Event {
        id
        name
        startDate
        endDate
        ${AppShowEventReferenceCode.fragments.eventFields}
        ${AppShowEventStatus.fragments.eventStatus}
        disciplineId
        discipline @client {
          id
          name
        }
        registrationEndDate
        countryId
        country @client {
          ${AppShowCountry.fragments.country}
        }
        organiser {
          id
          name
          logoPath
        }
        hostOrganisation {
          id
          name
        }
        regionId
        region {
          id
          name
        }
        place
      }
    `
  }
};
</script>
